import { FC, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Member } from '../../types/member';
import { updateMemberStatus } from '../../api/memberApi';
import { DashboardStatus } from '../../types/memberStatus';
import { FiArrowLeft, FiEdit2, FiTrash2 } from 'react-icons/fi';

interface AssistModalProps {
  isOpen: boolean;
  member?: Member;
  onClose: () => void;
  onStatusUpdate?: () => void;
}

interface Note {
  id: string;
  text: string;
  author: string;
  avatar: string;
  timestamp: Date;
}

const AssistModal: FC<AssistModalProps> = ({ isOpen, member, onClose, onStatusUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [view, setView] = useState<'main' | 'sms'>('main');
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteText, setNoteText] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);
  const [notesLoadedForMember, setNotesLoadedForMember] = useState<string | null>(null);

  const statusOptions = [
    DashboardStatus.DASHBOARD_READY,
    DashboardStatus.NEEDS_ATTENTION,
    DashboardStatus.AWAITING_DASHBOARD,
    DashboardStatus.PENDING
  ];

  useEffect(() => {
    if (member?.dashboard_status) {
      setSelectedStatus(member.dashboard_status);
    }
  }, [member]);

  useEffect(() => {
    if (member?.uuid) {
      const savedNotes = localStorage.getItem(`member_notes_${member.uuid}`);
      if (savedNotes) {
        try {
          const parsedNotes = JSON.parse(savedNotes);
          const notesWithDates = parsedNotes.map((note: Note) => ({
            ...note,
            timestamp: new Date(note.timestamp)
          }));
          setNotes(notesWithDates);
        } catch (error) {
          console.error('Error loading notes:', error);
          setNotes([]);
        }
      } else {
        setNotes([]);
      }
      setNotesLoadedForMember(member.uuid);
    }
  }, [member?.uuid]);

  useEffect(() => {
    if (member?.uuid && notesLoadedForMember === member.uuid) {
      if (notes.length > 0) {
        localStorage.setItem(`member_notes_${member.uuid}`, JSON.stringify(notes));
      } else {
        localStorage.removeItem(`member_notes_${member.uuid}`);
      }
      // Dispatch custom event to notify other components that notes have been updated
      window.dispatchEvent(new CustomEvent('memberNotesUpdated', { 
        detail: { memberUuid: member.uuid } 
      }));
    }
  }, [notes, member?.uuid, notesLoadedForMember]);

  const handleAddNote = () => {
    if (!noteText.trim()) return;

    const currentUserAvatar = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"%3E%3Ccircle cx="16" cy="16" r="16" fill="%233b82f6"/%3E%3Ctext x="16" y="21" text-anchor="middle" fill="white" font-size="12" font-weight="600" font-family="Arial, sans-serif"%3ESJ%3C/text%3E%3C/svg%3E';
    const currentUserName = 'Sarah Johnson';

    if (editingNoteId) {
      setNotes(notes.map(note => 
        note.id === editingNoteId 
          ? { ...note, text: noteText, timestamp: new Date() }
          : note
      ));
      setEditingNoteId(null);
    } else {
      const newNote: Note = {
        id: Date.now().toString(),
        text: noteText.trim(),
        author: currentUserName,
        avatar: currentUserAvatar,
        timestamp: new Date()
      };
      setNotes([newNote, ...notes]);
    }
    setNoteText('');
  };

  const handleEditNote = (note: Note) => {
    setNoteText(note.text);
    setEditingNoteId(note.id);
    noteInputRef.current?.focus();
  };

  const handleDeleteNote = (noteId: string) => {
    setNotes(notes.filter(note => note.id !== noteId));
  };

  const handleReviewProfile = () => {
    onClose();
    setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('view', 'members');
      window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
      window.dispatchEvent(new Event('popstate'));
      
      setTimeout(() => {
        const profileTab = document.querySelector('[title="View comprehensive member overview"]') as HTMLButtonElement;
        if (profileTab) {
          profileTab.click();
          setTimeout(() => {
            const profileCard = document.querySelector('[data-section="profile"]');
            if (profileCard) {
              profileCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        }
      }, 500);
    }, 100);
  };

  const handleSendSMS = () => {
    console.log('Sending SMS to:', member?.phone_number, 'Message:', smsMessage);
    setSmsMessage('');
    setView('main');
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
    if (!phoneNumber) return 'No phone number';
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phoneNumber;
  };

  const handleSaveAndClose = async () => {
    if (!member || !selectedStatus) {
      onClose();
      return;
    }

    if (selectedStatus !== member.dashboard_status) {
      setIsUpdating(true);
      try {
        await updateMemberStatus(member.uuid, selectedStatus);
        if (onStatusUpdate) {
          onStatusUpdate();
        }
        onClose();
      } catch (error) {
        console.error('Error updating member status:', error);
        alert('Failed to update status. Please try again.');
      } finally {
        setIsUpdating(false);
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = view === 'sms' ? (
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm transition-opacity" 
          onClick={() => setView('main')} 
        />

        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            className="relative rounded-xl max-w-lg w-full transform transition-all"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
            }}
          >
            <div 
              className="px-6 py-4 rounded-t-xl"
              style={{
                background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
                borderBottom: '1px solid rgba(203, 213, 225, 0.5)'
              }}
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setView('main')}
                  className="text-slate-400 hover:text-slate-600 transition-colors rounded-full p-1 hover:bg-slate-100"
                >
                  <FiArrowLeft className="h-5 w-5" />
                </button>
                <h3 className="text-lg font-medium text-slate-800">Send SMS</h3>
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    To: {formatPhoneNumber(member?.phone_number)}
                  </label>
                  <textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    rows={5}
                    className="w-full rounded-md text-sm border-slate-200 bg-white/80 focus:ring-brand-blue focus:border-brand-blue"
                    placeholder="Type your message..."
                    style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)' }}
                  />
                </div>
              </div>
            </div>

            <div 
              className="px-6 py-4 rounded-b-xl"
              style={{
                background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
                borderTop: '1px solid rgba(203, 213, 225, 0.5)'
              }}
            >
              <div className="flex justify-end items-center space-x-4">
                <button
                  onClick={() => setView('main')}
                  className="text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  cancel
                </button>
                <button 
                  onClick={handleSendSMS}
                  disabled={!smsMessage.trim()}
                  className="px-4 py-2 text-sm font-medium text-white rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(to bottom, #3b82f6, #2563eb)',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  Send SMS
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative rounded-xl max-w-lg w-full transform transition-all"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
          }}
        >
          <div 
            className="px-6 py-4 rounded-t-xl"
            style={{
              background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
              borderBottom: '1px solid rgba(203, 213, 225, 0.5)'
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-800">Assist Member</h3>
              <button 
                onClick={onClose} 
                className="text-slate-400 hover:text-slate-600 transition-colors rounded-full p-1 hover:bg-slate-100"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="px-6 py-5">
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Quick Actions</h4>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => setView('sms')}
                    className="px-3 py-2.5 rounded text-sm font-medium transition-all hover:bg-slate-50"
                    style={{
                      background: 'linear-gradient(to bottom, #f8fafc, #eef2f7)',
                      border: '1px solid rgba(203, 213, 225, 0.5)',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    Send SMS
                  </button>
                  <button 
                    onClick={handleReviewProfile}
                    className="px-3 py-2.5 rounded text-sm font-medium transition-all hover:bg-slate-50"
                    style={{
                      background: 'linear-gradient(to bottom, #f8fafc, #eef2f7)',
                      border: '1px solid rgba(203, 213, 225, 0.5)',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    Review Profile
                  </button>
                  <div className="col-span-2">
                    <label htmlFor="status-select" className="block text-xs font-medium text-slate-600 mb-1.5">
                      Update Status
                    </label>
                    <select
                      id="status-select"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      disabled={isUpdating}
                      className="w-full px-3 py-2.5 rounded text-sm font-medium transition-all border focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      style={{
                        background: 'linear-gradient(to bottom, #f8fafc, #eef2f7)',
                        border: '1px solid rgba(203, 213, 225, 0.5)',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {notes.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-700">Notes History</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {notes.map((note) => (
                      <div 
                        key={note.id}
                        className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200"
                      >
                        <img
                          src={note.avatar}
                          alt={note.author}
                          className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-slate-700">
                                  {note.author}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {formatTimestamp(note.timestamp)}
                                </span>
                              </div>
                              <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">
                                {note.text}
                              </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleEditNote(note)}
                                className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                                title="Edit note"
                              >
                                <FiEdit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                                title="Delete note"
                              >
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Add Note</h4>
                <textarea
                  ref={noteInputRef}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddNote();
                    }
                  }}
                  rows={3}
                  className="w-full rounded-md text-sm border-slate-200 bg-white/80 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="Enter notes about the assistance provided... (Press Enter to save)"
                  style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)' }}
                />
                <button
                  onClick={handleAddNote}
                  disabled={!noteText.trim()}
                  className="mt-3 w-full px-3 py-2.5 rounded text-sm font-medium transition-all hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(to bottom, #f8fafc, #eef2f7)',
                    border: '1px solid rgba(203, 213, 225, 0.5)',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {editingNoteId ? 'Update Note' : 'Save Note'}
                </button>
              </div>
            </div>
          </div>

          <div 
            className="px-6 py-4 rounded-b-xl"
            style={{
              background: 'linear-gradient(135deg, #f6f8fc, #e6eaf0)',
              borderTop: '1px solid rgba(203, 213, 225, 0.5)'
            }}
          >
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveAndClose}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? 'Saving...' : 'Save & Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AssistModal;
